/**
 * /saas-spend — Vendor-agnostic SaaS cost portfolio.
 * ===================================================
 * Auth-gated. Lets users add a SaaS cost source (pick a vendor template
 * or "custom"), paste a CSV, and see roll-up dashboards.
 *
 * 13 prebuilt templates: OpenAI, Anthropic, MongoDB Atlas, Vercel,
 * Cloudflare, Datadog, Confluent, Heroku, Fly.io, Supabase, Netlify,
 * Render + a generic Custom CSV.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Database, ArrowLeft, Plus, Upload, Trash2, DollarSign, Layers, FileSpreadsheet,
  Cloud, Brain, Network, HardDrive,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return s; } };

const CAT_ICON = {
  'AI / LLM': <Brain className="w-3.5 h-3.5" />,
  'Database': <Database className="w-3.5 h-3.5" />,
  'Database / BaaS': <Database className="w-3.5 h-3.5" />,
  'Hosting / Edge': <Cloud className="w-3.5 h-3.5" />,
  'CDN / Edge': <Cloud className="w-3.5 h-3.5" />,
  'Observability': <Network className="w-3.5 h-3.5" />,
  'Streaming / Kafka': <Network className="w-3.5 h-3.5" />,
  'PaaS': <Layers className="w-3.5 h-3.5" />,
  'Other': <HardDrive className="w-3.5 h-3.5" />,
};

const SaasSpend = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [sources, setSources] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // add source
  const [addOpen, setAddOpen] = useState(false);
  const [picked, setPicked] = useState(null); // template
  const [srcName, setSrcName] = useState('');
  const [csvText, setCsvText] = useState('');

  // detail
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, s, sum] = await Promise.all([
        axios.get(`${API}/saas-connectors/templates`),
        axios.get(`${API}/saas-connectors/sources`, auth()),
        axios.get(`${API}/saas-connectors/summary`, auth()),
      ]);
      setTemplates(t.data.templates || []);
      setSources(s.data.items || []);
      setSummary(sum.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) navigate('/login');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const onAddSource = async () => {
    if (!picked || !srcName) { toast.error('Pick a template + give it a name'); return; }
    try {
      const created = await axios.post(`${API}/saas-connectors/sources`, {
        vendor: picked.id, name: srcName, description: '',
      }, auth());
      if (csvText.trim()) {
        const up = await axios.post(
          `${API}/saas-connectors/sources/${created.data.source_id}/upload-csv`,
          { csv_text: csvText },
          auth(),
        );
        toast.success(`Imported ${up.data.rows_imported} rows · ${fmt(up.data.total_added_usd)}`);
      } else {
        toast.success(`Source "${srcName}" created. Add CSV later from the detail view.`);
      }
      setAddOpen(false);
      setPicked(null);
      setSrcName('');
      setCsvText('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this source and all imported cost rows?')) return;
    await axios.delete(`${API}/saas-connectors/sources/${id}`, auth()).catch(() => {});
    load();
  };

  const onOpenDetail = async (src) => {
    const { data } = await axios.get(`${API}/saas-connectors/sources/${src.source_id}`, auth());
    setDetail(data);
    setDetailOpen(true);
  };

  const grouped = templates.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="saas-spend-page">
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-slate-950 text-slate-100 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
          <Link to="/allocation" className="text-sm text-slate-400 hover:text-white">Cost Allocation →</Link>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3">
              <Database className="w-3 h-3 mr-1.5 inline" /> SaaS Spend Portfolio
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="saas-spend-h1"><BrandTitle>SaaS Spend</BrandTitle></h1>
            <p className="text-slate-400 mt-1">All your SaaS bills, one dashboard. 13 vendor templates · paste any CSV in 30 seconds.</p>
          </div>
          <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) { setPicked(null); setSrcName(''); setCsvText(''); } }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="saas-add-btn">
                <Plus className="w-4 h-4 mr-2" /> Add SaaS source
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{picked ? `Configure ${picked.name}` : 'Add a SaaS cost source'}</DialogTitle></DialogHeader>

              {!picked ? (
                <div className="space-y-5" data-testid="saas-template-picker">
                  {Object.entries(grouped).map(([cat, lst]) => (
                    <div key={cat}>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center gap-1.5">
                        {CAT_ICON[cat]} {cat}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {lst.map((t) => (
                          <button key={t.id} type="button" onClick={() => { setPicked(t); setSrcName(t.name); }} className="text-left p-3 rounded-lg border bg-slate-950 border-slate-800 hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-colors" data-testid={`saas-template-${t.id}`}>
                            <div className="font-bold text-slate-100">{t.name}</div>
                            <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  <button onClick={() => setPicked(null)} className="text-xs text-emerald-300 hover:underline">← back to templates</button>
                  <p className="text-xs text-slate-400">{picked.description}</p>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Source name</label>
                    <Input value={srcName} onChange={(e) => setSrcName(e.target.value)} className="mt-1 bg-slate-950 border-slate-800" placeholder='e.g. "Production account"' data-testid="saas-source-name" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Paste CSV (optional)</label>
                    <Textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} className="mt-1 bg-slate-950 border-slate-800 min-h-[180px] font-mono text-xs" placeholder={picked.sample_csv} data-testid="saas-csv-input" />
                    <p className="text-[10px] text-slate-500 mt-1">Expected columns: <code className="text-emerald-300">{Object.keys(picked.column_aliases).join(', ')}</code>. We auto-detect headers.</p>
                  </div>
                  <div className="text-[10px] bg-slate-950 border border-slate-800 rounded p-2 text-slate-400">
                    <div className="text-emerald-300 font-bold mb-1">Sample row:</div>
                    <pre className="font-mono text-[10px] whitespace-pre-wrap">{picked.sample_csv}</pre>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
                {picked && (
                  <Button onClick={onAddSource} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="saas-add-submit">
                    <Upload className="w-4 h-4 mr-2" /> {csvText.trim() ? 'Create + Import' : 'Create source'}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary cards */}
        {summary && summary.source_count > 0 && (
          <div className="grid sm:grid-cols-3 gap-3 mb-8">
            <Card className="bg-emerald-500/5 border-emerald-500/30">
              <CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold flex items-center gap-1.5"><DollarSign className="w-3 h-3" /> Total spend</div>
                <div className="text-3xl font-bold text-emerald-300 font-mono mt-1">{fmt(summary.total_usd)}</div>
                <div className="text-[10px] text-slate-400 mt-1">{summary.source_count} sources</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 text-slate-100 border-slate-800 col-span-2">
              <CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2 flex items-center gap-1.5"><Layers className="w-3 h-3" /> By category</div>
                <div className="space-y-1.5">
                  {(summary.by_category || []).map((c) => (
                    <div key={c.category} className="grid grid-cols-[140px_1fr_100px] items-center gap-3 text-xs" data-testid={`saas-cat-${c.category}`}>
                      <span className="text-slate-300 flex items-center gap-1.5">{CAT_ICON[c.category] || CAT_ICON.Other} {c.category}</span>
                      <div className="h-2 bg-slate-950 rounded overflow-hidden">
                        <div className="h-full bg-emerald-500/50" style={{ width: `${Math.round(c.share * 100)}%` }} />
                      </div>
                      <span className="text-emerald-300 font-mono text-right">{fmt(c.total_usd)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Source list */}
        {loading ? <p className="text-slate-500 text-sm">Loading…</p> :
          sources.length === 0 ? (
            <Card className="bg-slate-900 text-slate-100/30 border-slate-800 border-dashed">
              <CardContent className="p-12 text-center">
                <FileSpreadsheet className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-400">No SaaS sources yet.</p>
                <p className="text-slate-500 text-sm mt-1">Pick a template above and paste any CSV bill — we'll do the rest.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="saas-sources-list">
              {sources.map((s) => {
                const t = templates.find((x) => x.id === s.vendor);
                return (
                  <Card key={s.source_id} className="bg-slate-900 text-slate-100 border-slate-800 hover:border-slate-700 cursor-pointer transition-colors" onClick={() => onOpenDetail(s)} data-testid={`saas-source-card-${s.source_id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-slate-100 truncate">{s.name}</h3>
                            <Badge className="bg-slate-800 border-slate-700 text-slate-400 px-1.5 py-0 text-[9px]">{t?.name || s.vendor}</Badge>
                          </div>
                          {s.description && <p className="text-xs text-slate-500 line-clamp-1">{s.description}</p>}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); onDelete(s.source_id); }} className="text-slate-400 hover:text-rose-400 ml-3" data-testid={`saas-delete-${s.source_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        <div><div className="text-[9px] text-slate-500 uppercase">Total</div><div className="text-emerald-300 font-mono font-bold">{fmt(s.total_cost_usd)}</div></div>
                        <div><div className="text-[9px] text-slate-500 uppercase">Rows</div><div className="text-slate-400 font-mono">{s.item_count || 0}</div></div>
                        <div><div className="text-[9px] text-slate-500 uppercase">Imports</div><div className="text-slate-400 font-mono">{s.total_imports || 0}</div></div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

        {/* Detail */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-4xl">
            <DialogHeader><DialogTitle>{detail?.source?.name}</DialogTitle></DialogHeader>
            {detail && (
              <>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <Card className="bg-slate-950 text-slate-100 border-slate-800"><CardContent className="p-3"><div className="text-[10px] text-slate-500 uppercase">Total</div><div className="text-lg font-mono text-emerald-300">{fmt(detail.source.total_cost_usd)}</div></CardContent></Card>
                  <Card className="bg-slate-950 text-slate-100 border-slate-800"><CardContent className="p-3"><div className="text-[10px] text-slate-500 uppercase">Items</div><div className="text-lg font-mono text-slate-300">{detail.source.item_count}</div></CardContent></Card>
                  <Card className="bg-slate-950 text-slate-100 border-slate-800"><CardContent className="p-3"><div className="text-[10px] text-slate-500 uppercase">Imports</div><div className="text-lg font-mono text-slate-300">{detail.source.total_imports}</div></CardContent></Card>
                  <Card className="bg-slate-950 text-slate-100 border-slate-800"><CardContent className="p-3"><div className="text-[10px] text-slate-500 uppercase">Last import</div><div className="text-xs text-slate-300">{detail.source.last_import_at ? fmtDate(detail.source.last_import_at) : '—'}</div></CardContent></Card>
                </div>
                <div className="max-h-[55vh] overflow-y-auto">
                  <table className="w-full text-xs" data-testid="saas-detail-items">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-950 sticky top-0">
                        <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider text-slate-400">Date</th>
                        <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider text-slate-400">Service</th>
                        <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider text-slate-400">Description</th>
                        <th className="text-right px-3 py-2 text-[9px] uppercase tracking-wider text-slate-400">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).map((it, i) => (
                        <tr key={it.item_id || i} className="border-b border-slate-800/40">
                          <td className="px-3 py-1.5 text-slate-400 font-mono">{it.period_start}</td>
                          <td className="px-3 py-1.5 text-slate-700">{it.service || '—'}</td>
                          <td className="px-3 py-1.5 text-slate-400 truncate max-w-[200px]">{it.description || '—'}</td>
                          <td className="px-3 py-1.5 text-emerald-300 font-mono text-right">{fmt(it.amount_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default SaasSpend;
